import { useEffect, useRef } from 'react';
import { track } from './amplitude';

export const TOOLS = {
  bcba_goal_generator: 'BCBA Goal Generator',
  task_analysis_builder: 'Task Analysis Builder',
  goal_writing_assistant: 'Goal Writing Assistant',
  intervention_strategy_generator: 'Intervention Strategy Generator',
  progress_note_template_creator: 'Progress Note Template Creator',
  bip_template_generator: 'BIP Template Generator',
  session_note_summarizer: 'Session Note Summarizer',
  aba_assessment_template_generator: 'ABA Assessment Template Generator',
  data_collection_helper: 'Data Collection Helper',
};

function postShowPopup({ tool_id, tool_name, attempt_number }) {
  if (typeof window === 'undefined') return;
  if (window.parent === window) return; // not iframed; nothing to message
  try {
    window.parent.postMessage(
      {
        type: 'joon:show_signup_popup',
        tool_id,
        tool_name,
        attempt_number,
      },
      '*' // parent listener restricts by origin on receive
    );
  } catch (e) {
    // noop
  }
}

export function showLimitPopup({ tool_id, attempt_number, trigger }) {
  const tool_name = TOOLS[tool_id] || tool_id;
  track('tool:limit_modal_shown', {
    tool_id,
    tool_name,
    attempt_number,
    trigger,
  });
  postShowPopup({ tool_id, tool_name, attempt_number });
}

// Fire on mount: if user already at-or-over limit for this tool from a prior session, show popup once.
export function useMountUsageCheck(tool_id) {
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current || !tool_id) return;
    let cancelled = false;
    fetch('/api/usage', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const count = (data.counts && data.counts[tool_id]) || 0;
        track('tool:page_viewed', {
          tool_id,
          tool_name: TOOLS[tool_id] || tool_id,
          prior_uses: count,
          path: typeof window !== 'undefined' ? window.location.pathname : null,
        });
        if (count >= (data.limit || 1)) {
          fired.current = true;
          showLimitPopup({
            tool_id,
            attempt_number: count,
            trigger: 'pre_render',
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tool_id]);
}

// Call after a successful generate response. If `usage.show_popup`, fire popup.
export function handleGenerateUsage(tool_id, usage) {
  if (!usage) return;
  if (usage.show_popup) {
    showLimitPopup({
      tool_id,
      attempt_number: usage.count,
      trigger: 'post_attempt',
    });
  }
}

export function trackGenerationStarted(tool_id, props = {}) {
  track('tool:generation_started', {
    tool_id,
    tool_name: TOOLS[tool_id] || tool_id,
    ...props,
  });
}

export function trackGenerationCompleted(tool_id, props = {}) {
  track('tool:generation_completed', {
    tool_id,
    tool_name: TOOLS[tool_id] || tool_id,
    ...props,
  });
}

export function trackGenerationFailed(tool_id, props = {}) {
  track('tool:generation_failed', {
    tool_id,
    tool_name: TOOLS[tool_id] || tool_id,
    ...props,
  });
}

export function trackResultCopied(tool_id, props = {}) {
  track('tool:result_copied', {
    tool_id,
    tool_name: TOOLS[tool_id] || tool_id,
    ...props,
  });
}
